import React, { useState, useRef, useEffect } from "react";
import { useInput } from "react-admin";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FormHelperText, FormControl, TextField, InputAdornment, IconButton } from "@mui/material";
import { DateTime } from "luxon";
import EventIcon from "@mui/icons-material/Event";
import ClearIcon from "@mui/icons-material/Clear";
import "./DateInputFilter.css";
import { ar } from 'date-fns/locale';


const DateInputFilter = ({ source, fullWidth, disabled, field_meta }) => {
  let metaData = {};
  if (field_meta) {
    try {
      metaData = JSON.parse(field_meta);
    } catch (error) {
      console.error("Invalid JSON in field_meta:", error);
    }
  }

  const source_gte = `${source}@_gte`;
  const source_lt = `${source}@_lt`;

  const {
    field: { value: gteValue, onChange: setGte },
    fieldState: { error: gteError },
  } = useInput({ source: source_gte });

  const {
    field: { value: ltValue, onChange: setLt },
    fieldState: { error: ltError },
  } = useInput({ source: source_lt });

  const [dateRange, setDateRange] = useState([
    gteValue ? new Date(gteValue) : null,
    ltValue ? new Date(ltValue) : null,
  ]);

  const [startDate, endDate] = dateRange;
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const handleChange = (dates) => {
    const [start, end] = dates;
    setDateRange([start, end]);
  
    if (start) {
      const gteUtc = DateTime.fromJSDate(start, { zone: "Asia/Riyadh" })
        .startOf("day")
        .toUTC()
        .toISO();
  
      setGte(gteUtc);
    } else {
      setGte(undefined);
    }
  
    if (end) {
      const ltUtc = DateTime.fromJSDate(end, { zone: "Asia/Riyadh" })
        .endOf("day")
        .toUTC()
        .toISO();
  
      setLt(ltUtc);
    } else {
      setLt(undefined);
    }
  
    if (start && end) {
      setTimeout(() => setIsOpen(false), 200);
    }
  };
  

  const handleClear = (e) => {
    e.stopPropagation();
    setDateRange([null, null]);
    setGte(undefined);
    setLt(undefined);
  };

  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <FormControl
      ref={containerRef}
      fullWidth={fullWidth}
      error={!!gteError || !!ltError}
      disabled={disabled}
      className="custom-form-control"
      dir="rtl" // ✅ Add RTL support
    >
      <TextField
        variant="outlined"
        fullWidth
        placeholder={metaData.label || ""}
        value={
          startDate && endDate
            ? `${DateTime.fromJSDate(startDate).toFormat("yyyy-MM-dd")} - ${DateTime.fromJSDate(endDate).toFormat("yyyy-MM-dd")}`
            : ""
        }
        InputProps={{
          endAdornment: (
            <>
              {startDate && endDate && (
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              )}
              <InputAdornment position="end">
                <EventIcon />
              </InputAdornment>
            </>
          ),
        }}
        onClick={() => setIsOpen(true)} // ✅ Open calendar by clicking anywhere
        readOnly
      />

      <div className={`datepicker-container ${isOpen ? "visible" : "hidden"}`}>
        <DatePicker
          selected={startDate}
          onChange={handleChange}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          dateFormat="yyyy-MM-dd"
          inline
          locale={ar} // ✅ Set Arabic locale explicitly
          calendarStartDay={6} // Optional: Week starts on Saturday
        />
      </div>

      {(gteError || ltError) && (
        <FormHelperText>{gteError?.message || ltError?.message}</FormHelperText>
      )}
    </FormControl>
  );
};

export default DateInputFilter;
